import React, { useState, useEffect } from 'react';
import { useData } from '../DataContext';
import { Scissors, TrendingUp, TrendingDown, Users, Calendar, DollarSign } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://barbershopserver-production.up.railway.app/api';

const api = {
  getStats: async () => {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_BASE_URL}/stats`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response:', errorText);
      throw new Error(`Ошибка загрузки: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('API Response:', JSON.stringify(data, null, 2)); // Показываем полную структуру
    return data;
  }
};

const Dashboard = () => {
  const { clients } = useData();
  const [stats, setStats] = useState({
    income: 0,
    expenses: 0,
    profit: 0,
    totalClients: 0,
    haircutsCount: 0,
    todayHaircuts: 0,
    todayIncome: 0,
    todayProfit: 0,
    weeklyData: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStats();
  }, []);
  
  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.getStats();
      console.log('Full response:', JSON.stringify(response, null, 2)); // Логируем полный ответ
      
      // Обрабатываем разные варианты структуры ответа
      let statsData = response;
      
      // Если данные в response.data
      if (response && response.data) {
        statsData = response.data;
      }
      
      console.log('Stats data:', JSON.stringify(statsData, null, 2)); // Логируем обработанные данные
      
      // Устанавливаем состояние с проверкой на undefined
      setStats({
        income: statsData.income || statsData.totalIncome || 0,
        expenses: statsData.expenses || statsData.totalExpenses || 0,
        profit: statsData.profit || statsData.totalProfit || 0,
        totalClients: statsData.totalClients || 0,
        haircutsCount: statsData.haircutsCount || statsData.totalHaircuts || 0,
        todayHaircuts: statsData.todayHaircuts || 0,
        todayIncome: statsData.todayIncome || 0,
        todayProfit: statsData.todayProfit || 0,
        todayExpenses: statsData.todayExpenses || 0,
        weeklyData: statsData.weeklyData || statsData.weekly || []
      });
    } catch (error) {
      console.error('Ошибка загрузки статистики:', error);
      setError(error.message || 'Не удалось загрузить статистику');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    return new Intl.NumberFormat('uz-UZ').format(num);
  };

  // Считаем только реальных клиентов (не walk-in)
  const realClientsCount = clients.filter(client => !client.isWalkIn).length;

  const statCards = [
    {
      title: 'Общий доход ',
      value: `${formatNumber(stats.income)} сўм`,
      gradient: 'from-emerald-500 to-teal-600',
      icon: TrendingUp,
      bgColor: 'bg-emerald-500/10',
      iconColor: 'text-emerald-400'
    },
    {
      title: 'Расходы ',
      value: `${formatNumber(stats.expenses)} сўм`,
      gradient: 'from-rose-500 to-pink-600',
      icon: TrendingDown,
      bgColor: 'bg-rose-500/10',
      iconColor: 'text-rose-400'
    },
    {
      title: 'Прибыль ',
      value: `${formatNumber(stats.profit)} сўм`,
      gradient: 'from-blue-500 to-cyan-600',
      icon: DollarSign,
      bgColor: 'bg-blue-500/10',
      iconColor: 'text-blue-400'
    },
    {
      title: 'Всего клиентов',
      value: realClientsCount,
      gradient: 'from-purple-500 to-violet-600',
      icon: Users,
      bgColor: 'bg-purple-500/10',
      iconColor: 'text-purple-400'
    },
    {
      title: 'Стрижек ',
      value: stats.haircutsCount || 0,
      gradient: 'from-amber-500 to-orange-600',
      icon: Scissors,
      bgColor: 'bg-amber-500/10',
      iconColor: 'text-amber-400'
    }
  ];
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
          <p className="text-gray-400">Загрузка статистики...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-rose-500/10 border border-rose-500/50 rounded-xl p-6">
          <h3 className="text-rose-400 font-semibold mb-2">Ошибка загрузки</h3>
          <p className="text-gray-300 mb-2">{error}</p>
          <p className="text-gray-500 text-sm mb-4">Проверьте консоль браузера (F12) для деталей</p>
          <button 
            onClick={loadStats}
            className="px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-lg transition-colors"
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  console.log('Rendering with stats:', JSON.stringify(stats, null, 2)); // Логируем перед рендером

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Статистика</h1>
        <p className="text-gray-400">Обзор вашего бизнеса</p>
      </div>
      
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl hover:border-gray-600 transition-all duration-300 overflow-hidden group"
            >
              <div className={`h-1 bg-gradient-to-r ${stat.gradient}`}></div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-gray-400 text-sm font-medium">{stat.title}</h3>
                  <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`w-4 h-4 ${stat.iconColor}`} />
                  </div>
                </div>
                <p className="text-2xl font-bold text-white">{stat.value}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today Stats */}
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Calendar className="w-5 h-5 text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-white">Сегодня</h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-gray-700/30">
              <span className="text-gray-400 text-sm">Стрижек</span>
              <span className="text-white font-semibold">{stats.todayHaircuts || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-gray-700/30">
              <span className="text-gray-400 text-sm">Доход</span>
              <span className="text-emerald-400 font-semibold">{formatNumber(stats.todayIncome)} сўм</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-gray-700/30">
              <span className="text-gray-400 text-sm">Расходы</span>
              <span className="text-rose-400 font-semibold">{formatNumber(stats.todayExpenses || 0)} сўм</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-gray-700/30">
              <span className="text-gray-400 text-sm">Прибыль</span>
              <span className={`font-semibold ${stats.todayProfit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                {formatNumber(stats.todayProfit)} сўм
              </span>
            </div>
          </div>
        </div>

        {/* Weekly Chart */}
        <div className="lg:col-span-2 bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-6">Недельная активность</h3>
          {stats.weeklyData && stats.weeklyData.length > 0 ? (
            <div className="flex items-end justify-between h-48 gap-2">
              {stats.weeklyData.map((day, index) => {
                const maxIncome = Math.max(...stats.weeklyData.map(d => d.income || 0), 1);
                const height = maxIncome > 0 ? ((day.income || 0) / maxIncome) * 100 : 0;
                return (
                  <div key={index} className="flex-1 flex flex-col items-center gap-2">
                    <div className="w-full flex flex-col items-center justify-end flex-1">
                      <div className="text-xs text-gray-400 mb-1">{day.haircuts || 0}</div>
                      <div 
                        className="w-full bg-gradient-to-t from-blue-600 to-cyan-500 rounded-t-lg hover:from-blue-500 hover:to-cyan-400 transition-all duration-300"
                        style={{ 
                          height: `${height}%`,
                          minHeight: (day.income || 0) > 0 ? '4px' : '0'
                        }}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-500">{day.day || day.date || ''}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 text-gray-500">
              Нет данных за неделю
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;